import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useApproveBundle, useGetBundle, useUpdateBundle } from '~/hooks/apis/bundle/useBundles';

import { BUNDLE_STATUS_LABELS, Bundle } from '~/types/bundles/bundle';

import BundleDeparturesStep from '../../Bundles/BundleDeparturesStep';
import BundleItinerary from '../../Bundles/BundleItinerary';
import BundleOverview from '../../Bundles/BundleOverview';
import BundlePricesStep from '../../Bundles/BundlePricesStep';
import RenameBundleDialog from '../../Bundles/RenameBundleDialog';
import useBundleDepartureChecks from '../../Bundles/useBundleDepartureChecks';
import { BUNDLES_PATH } from '../../nativeTourModel';

const BUILD_STEP = 0;
const DEPARTURES_STEP = 1;
// Content is edited on the LTLE, which approval creates, so its step stays locked for now.
const PRICES_STEP = 3;

function BundleSteps({ bundle }: { bundle: Bundle }) {
  const { tour, hotels, departures, isChecking, checkError, blocked } = useBundleDepartureChecks(bundle);
  const isBuilt = !!tour && hotels.length > 0;
  const [activeStep, setActiveStep] = useState(isBuilt ? DEPARTURES_STEP : BUILD_STEP);

  const departuresNote =
    departures.length === 0
      ? 'None selected'
      : isChecking
        ? 'Checking…'
        : checkError
          ? "Couldn't check"
          : blocked.length > 0
            ? `${blocked.length} can't be sold`
            : `${departures.length} ready`;

  return (
    <Stack spacing={3}>
      <Card variant="outlined" sx={{ px: 2, py: 2.5 }}>
        <Stepper nonLinear activeStep={activeStep}>
          <Step completed={isBuilt}>
            <StepButton
              onClick={() => setActiveStep(BUILD_STEP)}
              optional={<Typography variant="caption">Tour and hotel</Typography>}
            >
              Build
            </StepButton>
          </Step>
          <Step completed={departures.length > 0 && !isChecking && !checkError && blocked.length === 0}>
            <StepButton onClick={() => setActiveStep(DEPARTURES_STEP)}>
              <StepLabel
                error={blocked.length > 0}
                optional={
                  <Typography variant="caption" color={blocked.length > 0 ? 'error' : undefined}>
                    {departuresNote}
                  </Typography>
                }
              >
                Departures
              </StepLabel>
            </StepButton>
          </Step>
          <Step disabled>
            <StepButton optional={<Typography variant="caption">After approval</Typography>}>Content</StepButton>
          </Step>
          <Step>
            <StepButton
              onClick={() => setActiveStep(PRICES_STEP)}
              optional={<Typography variant="caption">Adjust per departure</Typography>}
            >
              Prices
            </StepButton>
          </Step>
        </Stepper>
      </Card>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 8 }}>
          {activeStep === BUILD_STEP && (
            <Stack spacing={4}>
              <BundleItinerary bundle={bundle} />
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => setActiveStep(DEPARTURES_STEP)}
                  disabled={!tour}
                >
                  Continue to departures
                </Button>
              </Box>
            </Stack>
          )}
          {activeStep === DEPARTURES_STEP && (
            <Stack spacing={4}>
              <BundleDeparturesStep bundle={bundle} />
              <Stack direction="row" justifyContent="space-between">
                <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(BUILD_STEP)}>
                  Back to build
                </Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => setActiveStep(PRICES_STEP)}
                  disabled={departures.length === 0}
                >
                  Continue to prices
                </Button>
              </Stack>
            </Stack>
          )}
          {activeStep === PRICES_STEP && (
            <Stack spacing={4}>
              <BundlePricesStep bundle={bundle} />
              <Box>
                <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(DEPARTURES_STEP)}>
                  Back to departures
                </Button>
              </Box>
            </Stack>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <BundleOverview bundle={bundle} />
        </Grid>
      </Grid>
    </Stack>
  );
}

export default function TourBundlePage() {
  const { bundle_id: bundleId } = useParams<{ bundle_id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);

  const { data: bundle, isLoading, error } = useGetBundle(bundleId);
  const updateBundle = useUpdateBundle(bundleId);
  const approveBundle = useApproveBundle(bundleId);

  const approveBlocker = !bundle
    ? undefined
    : !bundle.components.some((component) => component.product_type === 'tour')
      ? 'Add a tour before approving'
      : !bundle.components.some((component) => component.product_type === 'flashAccommodation')
        ? 'Add a hotel before approving'
        : bundle.departure_dates.length === 0
          ? 'Choose departure dates before approving'
          : undefined;

  const handleMoveToDraft = async () => {
    try {
      await updateBundle.mutateAsync({ status: 'draft' });
      enqueueSnackbar('Bundle moved back to draft', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  const handleApprove = async () => {
    try {
      await approveBundle.mutateAsync(bundle);
      setIsApproveOpen(false);
      enqueueSnackbar('Bundle approved and its LTLE created', { variant: 'success' });
    } catch {
      // The mutation error is shown in the dialog.
    }
  };

  const closeApprove = () => {
    setIsApproveOpen(false);
    approveBundle.reset();
  };

  return (
    <Container maxWidth="xl">
      <PageHeader
        title={bundle?.name ?? 'Bundle'}
        backButton={BUNDLES_PATH}
        titleActions={
          bundle && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Rename">
                <IconButton size="small" aria-label="Rename bundle" onClick={() => setIsRenameOpen(true)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Chip
                size="small"
                label={BUNDLE_STATUS_LABELS[bundle.status]}
                color={bundle.status === 'approved' ? 'success' : 'default'}
              />
            </Stack>
          )
        }
      >
        <Box pb={1}>
          {bundle?.status === 'draft' && (
            <Tooltip title={approveBlocker ?? ''}>
              {/* Disabled buttons fire no events, so the tooltip needs a wrapper. */}
              <span>
                <Button
                  variant="contained"
                  onClick={() => setIsApproveOpen(true)}
                  disabled={!!approveBlocker || approveBundle.isPending}
                >
                  Approve
                </Button>
              </span>
            </Tooltip>
          )}
          {bundle?.status === 'approved' && (
            <Button onClick={handleMoveToDraft} disabled={updateBundle.isPending}>
              Move back to draft
            </Button>
          )}
        </Box>
      </PageHeader>

      {isLoading && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!error && <Alert severity="error">{error.message}</Alert>}

      {!!bundle && (
        <>
          <BundleSteps bundle={bundle} />
          <RenameBundleDialog bundle={bundle} open={isRenameOpen} onClose={() => setIsRenameOpen(false)} />
        </>
      )}

      <Dialog open={isApproveOpen} onClose={approveBundle.isPending ? undefined : closeApprove}>
        <DialogTitle>Approve bundle?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              Approving creates the bundle's limited time LE tour (LTLE) from its base tour, ready for its content. The
              tour, hotel and departures can't be changed once approved. Price adjustments still can.
            </DialogContentText>
            {!!approveBundle.error && (
              <Alert severity="error">
                {approveBundle.error.message} Nothing is lost: approving again carries on from where it stopped.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeApprove} disabled={approveBundle.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApprove} disabled={approveBundle.isPending}>
            {approveBundle.isPending ? 'Approving…' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
